<template>
  <div v-if="product && productPrice">
    <define-eyebrow-template>
      <div v-if="product.eyebrow">
        <vf-toggletip v-if="product.eyebrow.details">
          <template #trigger>
            <span class="flex gap-1 items-center">
              <p v-bind="eyebrowStyle" class="eyebrow line-clamp-1">
                {{ product.eyebrow.label }}
              </p>
              <vf-icon name="info" />
            </span>
          </template>
          {{ product.eyebrow.details }}
        </vf-toggletip>
        <p v-else-if="product.eyebrow.label" v-bind="eyebrowStyle" class="eyebrow line-clamp-1">
          {{ product.eyebrow.label }}
        </p>
      </div>
    </define-eyebrow-template>
    <define-favorites-template>
      <vf-button
        v-if="
          selectedVariant?.notifyMe
            && $feature.showNotifyMeOnPDP && isSelectedProductOutOfStock
            ? (!favoriteCta.hideWhenNotifyMe && $feature.catalogFavorites)
            : $feature.catalogFavorites
        "
        :aria-label="isFavorite ? $t.removeFromFavorites : $t.favorites"
        class="p-0 b-0 h-10 w-10"
        :data-test-id="`pdp-${isFavorite ? 'remove' : 'add'}-favorites-cta`"
        disable-success-icon
        :disabled="favorites.error"
        :loading="isFavoritePending"
        :size="ctaSize"
        :variant="isFavorite ? favoriteCta.variantSelected : 'transparent'"
        @click="toggleFavorites(productColor?.label)"
      >
        <vf-icon
          :class="{ 'c-brand-3': isFavorite }"
          :name="isFavorite ? 'heart-filled' : 'heart'"
          :size="favoriteCta.iconSize"
        />
        <span v-if="!favoriteCta.iconOnly" class="ml-2">{{ $t.favorites }}</span>
      </vf-button>
    </define-favorites-template>

    <div class="flex gap-4 between">
      <eyebrow-template v-if="!$feature.showBadgeAboveTitle" />
      <pdp-section-breadcrumbs class="ml-a" />
    </div>
    <div class="flex items-center relative between">
      <div>
        <h1 class="body-2 mt-1" data-test-id="product-name">
          {{ product.name }}
        </h1>
        <transition name="slide">
          <p
            v-if="$feature.showShortDescriptionOnPDP && product.shortDescription"
            class="body-4 c-grey-30"
            :class="{ '<lg:hidden': !isDrawerExpanded }"
            style="margin-top: 0.125rem"
          >
            {{ product.shortDescription }}
          </p>
        </transition>
        <eyebrow-template v-if="$feature.showBadgeAboveTitle" style="margin-top: 0.125rem" />
      </div>
      <favorites-template class="<lg:hidden" />
    </div>

    <div class="mt-1 flex items-center space-x-1 between">
      <product-pricing
        class="body-3"
        :currency="product.currency"
        :price="productPrice"
        :show-discount-percentage="$feature.showDiscountPercentageOnCatalog"
        :show-omnibus-details="isDrawerExpanded"
        :show-original-price-for-omnibus="$feature.showOmnibusBadgeOnQuickshopAndPDP"
      >
        <template #append>
          <vf-tooltip v-if="salesPriceActiveDate" class="-ml-2" :class="brandClasses.sale">
            <template #trigger>
              <vf-icon name="info" />
            </template>
            {{ replaceAll($t.salesPriceActiveDateInfo, { date: formattedSalesDate }) }}
          </vf-tooltip>
        </template>
      </product-pricing>
      <favorites-template class="lg:hidden" />
    </div>
    <div
      ref="promotionRef"
      class="body-4 truncate space-y-2"
      :class="{ 'mt-2': !!promotionDivHeight, '<lg:hidden': !isDrawerExpanded }"
      style="min-height: 1.5rem"
    >
      <p v-if="product.promoExclusionsMessage">
        {{ product.promoExclusionsMessage }}
      </p>
      <div id="below-price-info" />
      <p v-if="isComingSoon" class="py-1">
        <span class="px-2 py-1 rounded-xs bg-grey-90">{{ comingSoonText }}</span>
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { formatDate } from '@vueuse/core'

defineProps<{
  isDrawerExpanded?: boolean
}>()

const { brandClasses } = useAppConfig().components.product.pricing

const { details: { favoriteCta, ctaSize } } = useAppConfig().pages.pdp
const favorites = useFavoritesStore()
const { $feature, $t } = useNuxtApp()
const {
  eyebrowStyle,
  isComingSoon,
  product,
  productPrice,
  selectedVariant,
  isFavorite,
  isFavoritePending,
  isSelectedProductOutOfStock,
  toggleFavorites,
  productColor
} = usePdp()

const promotionRef = ref()
const { height: promotionDivHeight } = useElementBounding(promotionRef)

const [DefineEyebrowTemplate, EyebrowTemplate] = createReusableTemplate()
const [DefineFavoritesTemplate, FavoritesTemplate] = createReusableTemplate()

const salesPriceActiveDate = computed(() =>
  product.value && getDefaultColorOption(product.value.attributes, product.value.id).price?.salesPriceActiveDate)

const formattedSalesDate = computed(() => salesPriceActiveDate.value ? formatDate(new Date(salesPriceActiveDate.value), 'MM/DD') : '')

const comingSoonText = computed(() => {
  if (product.value?.targetPublishDate) {
    const formattedDate = formatDate(
      new Date(product.value.targetPublishDate.replace(/-/g, '\/')),
      $feature.configProductComingSoonDateFormat
    )
    return `${$t.comingSoon} – ${formattedDate}`
  }

  return $t.comingSoon
})
</script>
